import { loginBg } from "@/constants";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { api } from "@/services/api.service";
import { useState } from "react";
import { AxiosError } from "axios";

const resetPasswordSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required.")
    .email({ message: "Please enter a valid email address." }),
});

type ResetPasswordInputs = z.infer<typeof resetPasswordSchema>;

interface PasswordResetStep2 {
  setStep: React.Dispatch<React.SetStateAction<number>>;
}

export const Step1 = ({ setStep }: PasswordResetStep2) => {
  const [emailError, setEmailError] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordInputs>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit: SubmitHandler<ResetPasswordInputs> = async (data) => {
    try {
      // Here you make the API call to request a password reset key
      const response = await api.post("/reset-password/requestKey", {
        email: data.email,
      });
      console.log(response.data);
      setStep(3);
    } catch (error) {
      console.error(error);
      if ((error as AxiosError).response?.status === 404)
        setEmailError("This Email is Not Registered. Please Check.");
    }
  };

  // to reset email error message when the input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    // Clear corresponding error message when input changes
    if (name === "email") setEmailError("");
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 `}>
        {/* Left Column for the Form */}
        <div className={`hidden h-full w-full md:block `}>
          <img
            src={loginBg}
            alt="CryptoDrop"
            className="h-full w-full rounded-lg object-fill"
          />
        </div>
        {/* Right Column for the Image */}
        <div className="flex h-full w-full flex-col justify-center rounded-l-lg p-2 text-white ">
          <div className="mb-5 font-['Inter'] text-sm font-normal text-white">
            Steps 01 / 02
          </div>
          <div className="mb-2 font-['Inter'] text-2xl font-medium text-white">
            Password Reset
          </div>
          <div className="mb-2 font-['Montserrat'] text-base font-normal leading-tight text-white">
            Request password reset by confirming your account. Please enter your
            registered email in the field below and we’ll send you a link to
            reset your password
          </div>

          <div className="mb-4 mt-3 w-full">
            <label
              htmlFor="email"
              className="mb-1 block text-xs font-medium text-white sm:text-sm"
            >
              Registered Email
            </label>
            <input
              id="email"
              // type="email"
              placeholder="user@mail.com"
              className="mt-1 w-full rounded-md border-2 border-solid border-white/5 bg-white/5 px-3 py-2 text-white sm:text-sm"
              {...register("email")}
              onChange={handleInputChange} // Add onChange event handler
            />
            {errors.email ? (
              <p className="mt-1.5 text-[10px] font-normal text-[#FF6780]">
                {errors.email.message}
              </p>
            ) : (
              emailError && (
                <p className="mt-1.5 text-[10px] font-normal text-[#FF6780]">
                  {emailError}
                </p>
              )
            )}
          </div>
          <Button
            type="submit"
            className="mt-3 flex w-full rounded-md bg-gradient-to-r from-[#003BD2] to-[#00FFC2] py-5"
          >
            Send
          </Button>
        </div>
      </div>
    </form>
  );
};
